import {
  Tabs,
  TabsList,
  TabsListItem,
  TabsTrigger,
  TabsContent,
} from "~/react/ui/tabs"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "~/react/ui/button"
import { OrderSummaryPopover } from "~/react/components/email_accounts/order-summary-popover"
import DomainsTab from "~/react/components/email_accounts/tabs/domains-tab"
import AccountsTab from "~/react/components/email_accounts/tabs/accounts-tab"
import BillingTab from "~/react/components/email_accounts/tabs/billing-tab"
import CheckoutTab from "~/react/components/email_accounts/tabs/checkout-tab"
import {
  computeNextDisabledReason,
  validateManualAccount,
  validateAutoMode,
} from "~/react/components/email_accounts/validation"
import {
  type EmailAccountsProvisioningProps,
  type PreviewRow,
  type ManualAccount,
  type ManualSender,
  type Tab,
  type Suggestion,
  type DomainOwnerForm,
  type DomainOwner,
} from "~/react/components/email_accounts/types"
import {
  getStorageKey,
  generateUUID,
  mergeSuggestionsIntoPlaceholders,
  getErrorMessageFromError,
} from "~/react/components/email_accounts/utils"
import { useProvisioningStorage } from "~/react/hooks/useProvisioningStorage"
import { useDomainSuggestions } from "~/react/hooks/useDomainSuggestions"
import { useTabNavigation } from "~/react/hooks/useTabNavigation"
import { useAccountPreview } from "~/react/hooks/useAccountPreview"

const EmailAccountsProvisioning = ({
  teamMembers = [],
  user,
  webmailDomains = [
    "gmail.com",
    "googlemail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
  ],
  countryOptions = [],
  domainOwner: initialDomainOwner,
  billingInformation,
  creditCard,
  currencyCode,
  googlePriceCents,
  flowMode = "full",
  existingDomains = [],
  stripePublicKey,
  existingMailboxes = [],
}: EmailAccountsProvisioningProps) => {
  const [domainsCount, setDomainsCount] = useState<number>(1)
  const [baseDomain, setBaseDomain] = useState<string>("")
  const [tab, setTab] = useState<Tab>("domain")
  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({})
  const [flowError, setFlowError] = useState<string | undefined>(undefined)
  const [showManualForm, setShowManualForm] = useState<boolean>(false)
  const [manualDomain, setManualDomain] = useState<string>("")
  const [isCheckingManual, setIsCheckingManual] = useState<boolean>(false)
  const [manualError, setManualError] = useState<string | undefined>(undefined)

  const {
    suggestions,
    setSuggestions,
    setSuggestionsOffset,
    isGenerating,
    setIsGenerating,
    errorTimeoutRef: domainErrorTimeoutRef,
    generateSuggestions: generateDomainSuggestions,
    addManualDomain: addManualDomainToSuggestions,
  } = useDomainSuggestions()

  const {
    goToNextTab: getNextTab,
    isStepCompleted,
    getStepNumber,
  } = useTabNavigation(flowMode)
  const [redirectUrl, setRedirectUrl] = useState<string>("")
  const [isWebmail, setIsWebmail] = useState<boolean>(true)
  const [hasInitialized, setHasInitialized] = useState<boolean>(false)
  const previousBaseDomainRef = useRef<string>("")
  const suggestionsRef = useRef<Suggestion[]>([])
  const previousExistingDomainsRef = useRef<string>("")
  const [manualMode, setManualMode] = useState<boolean>(false)
  const [selectedSenders, setSelectedSenders] = useState<
    Record<string, boolean>
  >({})
  const [emailAccountsPerSender, setEmailAccountsPerSender] =
    useState<number>(1)
  const [signature, setSignature] = useState<string>("")
  const [manualSenders, setManualSenders] = useState<ManualSender[]>([])
  const [manualFirst, setManualFirst] = useState<string>("")
  const [manualLast, setManualLast] = useState<string>("")
  const [showManualSenderForm, setShowManualSenderForm] =
    useState<boolean>(false)
  const [manualSenderError, setManualSenderError] = useState<
    string | undefined
  >(undefined)
  const [manualAccounts, setManualAccounts] = useState<ManualAccount[]>([
    {
      id: `${Date.now()}`,
      first: "",
      last: "",
      local: "",
      domain: "",
      signature: "",
      open: true,
      status: "draft",
    },
  ])
  const [preview, setPreview] = useState<PreviewRow[]>([])
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [accountGenerationMap, setAccountGenerationMap] = useState<
    Record<string, boolean>
  >({})

  // Domain owner tab state
  const [domainOwner, setDomainOwner] = useState<DomainOwner | null>(
    initialDomainOwner || null,
  )
  const [domainOwnerConfirmed, setDomainOwnerConfirmed] =
    useState<boolean>(false)
  const [editingDomainOwner, setEditingDomainOwner] = useState<boolean>(false)
  const [domainOwnerForm, setDomainOwnerForm] = useState<DomainOwnerForm>({
    firstName: initialDomainOwner?.first_name || user?.first_name || "",
    lastName: initialDomainOwner?.last_name || user?.last_name || "",
    email: initialDomainOwner?.email || user?.email || "",
    company:
      initialDomainOwner?.company || billingInformation?.company_name || "",
    address: initialDomainOwner?.address || billingInformation?.address || "",
    postalCode:
      initialDomainOwner?.postal_code || billingInformation?.postal_code || "",
    city: initialDomainOwner?.city || billingInformation?.city || "",
    countryCode:
      initialDomainOwner?.country_code ||
      billingInformation?.country_code ||
      "",
  })
  const [savingDomainOwner, setSavingDomainOwner] = useState<boolean>(false)

  // Billing and payment information state (managed locally, updated after saves)
  const [currentBillingInformation, setCurrentBillingInformation] =
    useState(billingInformation)
  const [currentCreditCard, setCurrentCreditCard] = useState(creditCard)

  // Billing and payment form state (not saved until next is clicked)
  const [billingForm, setBillingForm] = useState({
    companyName: billingInformation?.company_name || "",
    address: billingInformation?.address || "",
    postalCode: billingInformation?.postal_code || "",
    city: billingInformation?.city || "",
    countryCode: billingInformation?.country_code || "",
    additionalInfo: billingInformation?.additional_info || "",
  })
  const [paymentForm, setPaymentForm] = useState({
    nameOnCard: creditCard?.full_name || "",
  })
  const [editingBilling, setEditingBilling] =
    useState<boolean>(!billingInformation)
  const [editingPayment, setEditingPayment] = useState<boolean>(!creditCard)

  const storageKey = useMemo(() => getStorageKey(flowMode), [flowMode])

  const resetAll = () => {
    try {
      localStorage.removeItem(storageKey)
    } catch (e) {}
    setDomainsCount(1)
    setBaseDomain("")
    setSuggestions([])
    setSuggestionsOffset(0)
    setSelectedMap({})
    setShowManualForm(false)
    setManualDomain("")
    setRedirectUrl("")
    setTab("domain")
    setPreview([])
    setShowPreview(false)
    setManualMode(false)
    setManualAccounts([
      {
        id: `${Date.now()}`,
        first: "",
        last: "",
        local: "",
        domain: "",
        signature: "",
        open: true,
        status: "draft",
      },
    ])
    setAccountGenerationMap({})
    setSelectedSenders({})
    setManualSenders([])
    setShowManualSenderForm(false)
    setManualFirst("")
    setManualLast("")
    setManualSenderError(undefined)
    setFlowError(undefined)
    setManualError(undefined)
    setEmailAccountsPerSender(1)
    setSignature("")
  }

  useProvisioningStorage({
    storageKey,
    domainsCount,
    baseDomain,
    suggestions,
    selectedMap,
    showManualForm,
    manualDomain,
    redirectUrl,
    tab,
    preview,
    showPreview,
    manualMode,
    manualAccounts,
    setDomainsCount,
    setBaseDomain,
    setSuggestions,
    setSelectedMap,
    setShowManualForm,
    setManualDomain,
    setRedirectUrl,
    setTab,
    setPreview,
    setShowPreview,
    setManualMode,
    setManualAccounts,
  })

  // Cleanup: Clear localStorage when component unmounts (user leaves the flow)
  // State will naturally reset when component remounts, so we only need to clear persistence
  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem(storageKey)
      } catch (e) {
        // Ignore errors when clearing localStorage
      }
    }
  }, [storageKey])

  const selectedDomains = useMemo(
    () =>
      suggestions
        .filter((s) => s.status === "ready" && selectedMap[s.id])
        .map((s) => s.domain as string),
    [suggestions, selectedMap],
  )

  // Filter out existing domains (domains that don't need to be purchased)
  const newDomainsForCheckout = useMemo(
    () =>
      suggestions
        .filter(
          (s) =>
            s.status === "ready" &&
            selectedMap[s.id] &&
            !s.id.startsWith("existing-"),
        )
        .map((s) => s.domain as string),
    [suggestions, selectedMap],
  )

  // Only include new domains (not existing) in price calculation
  const newDomainsWithPrices = useMemo(
    () =>
      suggestions
        .filter(
          (s) =>
            s.status === "ready" &&
            selectedMap[s.id] &&
            !s.id.startsWith("existing-"),
        )
        .map((s) => ({ domain: s.domain as string, priceCents: s.priceCents })),
    [suggestions, selectedMap],
  )

  const totalPriceCents = useMemo(
    () =>
      newDomainsWithPrices.reduce(
        (sum, item) => sum + (item.priceCents || 0),
        0,
      ),
    [newDomainsWithPrices],
  )

  // Domains to use for account generation (can be subset of selectedDomains)
  const accountGenerationDomains = useMemo(
    () =>
      suggestions
        .filter((s) => s.status === "ready" && accountGenerationMap[s.id])
        .map((s) => s.domain as string),
    [suggestions, accountGenerationMap],
  )

  // Restore previous state (including suggestions) from localStorage, if any
  useEffect(() => {
    if (hasInitialized) return

    try {
      if (
        typeof window === "undefined" ||
        typeof window.localStorage === "undefined"
      ) {
        // No localStorage available, allow auto-prefill to run
        return
      }
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) {
        // No persisted data, allow auto-prefill to run
        return
      }

      const parsed = JSON.parse(raw) as {
        baseDomain?: string
        domainsCount?: number
        suggestions?: Suggestion[]
        selectedMap?: Record<string, boolean>
        showManualForm?: boolean
        manualDomain?: string
        redirectUrl?: string
        tab?: Tab
      }

      if (parsed.suggestions && parsed.suggestions.length > 0) {
        // We have persisted suggestions, restore them and prevent auto-prefill
        if (parsed.baseDomain !== undefined) setBaseDomain(parsed.baseDomain)
        if (parsed.domainsCount !== undefined)
          setDomainsCount(parsed.domainsCount)
        if (parsed.suggestions !== undefined) setSuggestions(parsed.suggestions)
        if (parsed.selectedMap !== undefined) setSelectedMap(parsed.selectedMap)
        if (parsed.showManualForm !== undefined)
          setShowManualForm(parsed.showManualForm)
        if (parsed.manualDomain !== undefined)
          setManualDomain(parsed.manualDomain)
        if (parsed.redirectUrl !== undefined) setRedirectUrl(parsed.redirectUrl)
        if (parsed.tab !== undefined) setTab(parsed.tab)

        setHasInitialized(true)
      }
      // If no suggestions in localStorage, don't set hasInitialized - allow auto-prefill to run
    } catch (error) {
      // If localStorage data is corrupted, clear it and start fresh
      console.warn("Failed to restore state from localStorage:", error)
      try {
        window.localStorage.removeItem(storageKey)
      } catch {
        // Ignore errors when removing corrupted data
      }
    }
  }, [hasInitialized, storageKey])

  useEffect(() => {
    suggestionsRef.current = suggestions
  }, [suggestions])

  useEffect(() => {
    suggestionsRef.current = suggestions
  }, [suggestions])

  useEffect(() => {
    suggestionsRef.current = suggestions
  }, [suggestions])

  useEffect(() => {
    const purchaseIds = new Set(
      suggestions
        .filter((s) => s.status === "ready" && selectedMap[s.id])
        .map((s) => s.id),
    )
    setAccountGenerationMap((prev) => {
      const next = { ...prev }
      // Add newly selected domains
      purchaseIds.forEach((id) => {
        if (!(id in next)) {
          next[id] = true
        }
      })
      // Remove domains that are no longer selected for purchase
      Object.keys(next).forEach((id) => {
        if (!purchaseIds.has(id)) {
          delete next[id]
        }
      })
      return next
    })
  }, [suggestions, selectedMap])

  // Add existing domains to suggestions when provided, and remove deleted ones
  useEffect(() => {
    // Create a stable string representation to compare
    const existingDomainsKey = existingDomains
      .map((d) => d.toLowerCase())
      .sort()
      .join(",")

    // Only update if existingDomains actually changed
    if (previousExistingDomainsRef.current === existingDomainsKey) {
      return
    }

    previousExistingDomainsRef.current = existingDomainsKey

    if (existingDomains.length === 0) {
      // If no existing domains, just remove any existing- suggestions
      setSuggestions((prev) =>
        prev.filter((s) => !s.id.startsWith("existing-")),
      )
      setSelectedMap((prevMap) => {
        const next = { ...prevMap }
        Object.keys(next).forEach((id) => {
          if (id.startsWith("existing-")) {
            delete next[id]
          }
        })
        return next
      })
      return
    }

    const existingDomainsSet = new Set(
      existingDomains.map((d) => d.toLowerCase()),
    )

    // Update suggestions (no nested state updates)
    setSuggestions((prev) => {
      // Remove existing domain suggestions that are no longer in existingDomains
      const filtered = prev.filter((s) => {
        if (s.id.startsWith("existing-")) {
          const domain = (s.domain || "").toLowerCase()
          return existingDomainsSet.has(domain)
        }
        return true
      })

      // Add existing domains that aren't already in suggestions
      const existingInSuggestions = new Set(
        filtered
          .filter((s) => s.status === "ready" && s.domain)
          .map((s) => (s.domain as string).toLowerCase()),
      )

      const newSuggestions = [...filtered]
      existingDomains.forEach((domain) => {
        const normalized = domain.toLowerCase()
        if (!existingInSuggestions.has(normalized)) {
          const id = `existing-${domain}`
          // Check if this ID already exists
          if (!newSuggestions.some((s) => s.id === id)) {
            newSuggestions.push({
              id,
              status: "ready",
              domain,
              priceCents: 0, // Existing domains don't have a price
            })
          }
        }
      })

      return newSuggestions
    })

    // Update selectedMap separately (avoid nested state updates)
    setSelectedMap((prevMap) => {
      const next = { ...prevMap }
      let hasChanges = false

      // Remove selections for deleted existing domains
      Object.keys(next).forEach((id) => {
        if (id.startsWith("existing-")) {
          const domain = id.replace("existing-", "").toLowerCase()
          if (!existingDomainsSet.has(domain)) {
            delete next[id]
            hasChanges = true
          }
        }
      })

      // Auto-select new existing domains
      existingDomains.forEach((domain) => {
        const id = `existing-${domain}`
        if (!next[id]) {
          next[id] = true
          hasChanges = true
        }
      })

      // Only return new object if there were changes
      return hasChanges ? next : prevMap
    })
  }, [existingDomains, setSuggestions, setSelectedMap])

  // Reset offset when base domain changes
  useEffect(() => {
    setSuggestionsOffset(0)
  }, [baseDomain, setSuggestionsOffset])

  useEffect(() => {
    if (!baseDomain?.trim() && isGenerating) {
      setIsGenerating(false)
    }
  }, [baseDomain, domainsCount, isGenerating])

  useEffect(() => {
    const normalizedDomain = baseDomain.trim().toLowerCase()
    const previousDomain = previousBaseDomainRef.current

    if (normalizedDomain === previousDomain) {
      return
    }

    previousBaseDomainRef.current = normalizedDomain

    if (!normalizedDomain) return

    const currentRedirectUrl = redirectUrl.trim()

    if (!currentRedirectUrl) {
      setRedirectUrl(`https://${normalizedDomain}`)
      return
    }

    if (previousDomain) {
      try {
        const url = new URL(currentRedirectUrl)
        const urlDomain = url.hostname.replace(/^www\./, "").toLowerCase()

        if (urlDomain === previousDomain) {
          setRedirectUrl(`https://${normalizedDomain}`)
        }
      } catch {
        const urlMatch = currentRedirectUrl.match(
          /^https?:\/\/(?:www\.)?([^\/]+)/i,
        )
        if (urlMatch) {
          const urlDomain = urlMatch[1].toLowerCase()
          if (urlDomain === previousDomain) {
            setRedirectUrl(`https://${normalizedDomain}`)
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDomain])

  useEffect(() => {
    if (hasInitialized || !user?.email) return

    const email = user.email.trim().toLowerCase()
    const emailMatch = email.match(/@(.+)$/)

    if (emailMatch) {
      const domain = emailMatch[1]

      if (!webmailDomains.includes(domain)) {
        setIsWebmail(false)
        setBaseDomain(domain)
        setRedirectUrl(`https://${domain}`)
        setDomainsCount(3)
        const timer = setTimeout(() => {
          if (domain) {
            const generateWithDomain = async () => {
              // Check if suggestions already exist before generating (using ref to get current value)
              const currentSuggestions = suggestionsRef.current.filter(
                (s) => s.status === "ready",
              )
              if (currentSuggestions.length > 0) {
                return
              }

              setIsGenerating(true)
              const desired = 3
              const placeholders: Suggestion[] = Array.from({
                length: desired,
              }).map(() => ({
                id: `placeholder-${generateUUID()}`,
                status: "loading",
              }))
              setSuggestions(placeholders)

              try {
                const response = await fetch("/mailpool/domain_suggestions", {
                  method: "POST",
                  headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-CSRF-Token":
                      document
                        .querySelector('meta[name="csrf-token"]')
                        ?.getAttribute("content") || "",
                  },
                  body: JSON.stringify({
                    base_domain: domain,
                    count: desired,
                    offset: 0,
                  }),
                })
                if (response.ok) {
                  const data: {
                    suggestions: Array<{ domain: string; price_cents: number }>
                    empty?: boolean
                  } = await response.json()
                  setSuggestions((prev) => {
                    const placeholderIds = new Set(
                      placeholders.map((p) => p.id),
                    )
                    return mergeSuggestionsIntoPlaceholders(
                      prev,
                      placeholderIds,
                      data.suggestions || [],
                    )
                  })
                  setSuggestionsOffset(data.suggestions?.length || 0)
                }
              } catch (e) {
                const errorMessage = getErrorMessageFromError(
                  e,
                  window.i18n.t("js.email_accounts.provisioning.api_error"),
                )
                const App = (window as any).App
                if (App && typeof App.displayFlash === "function") {
                  App.displayFlash("error", errorMessage)
                }
                setSuggestions((prev) =>
                  prev.map((s) =>
                    s.status === "loading" ? { ...s, status: "error" } : s,
                  ),
                )
              } finally {
                setIsGenerating(false)
              }
            }
            generateWithDomain()
          }
        }, 200)
        return () => clearTimeout(timer)
      }
    }
    setHasInitialized(true)
  }, [user?.email, hasInitialized])

  useEffect(() => {
    return () => {
      if (domainErrorTimeoutRef.current) {
        clearTimeout(domainErrorTimeoutRef.current)
      }
    }
  }, [domainErrorTimeoutRef])

  const selectedSenderInfos = useMemo(() => {
    const infos: Array<{ id: string; first: string; last: string }> = []
    const norm = (s: string) => s.trim()
    // team members
    teamMembers.forEach((m) => {
      const key = `${m.id}`
      if (selectedSenders[key]) {
        const fullName = m.full_name || ""
        const parts = fullName.split(/\s+/)
        const first = norm(parts[0] || m.first_name || "")
        const last = norm(
          parts.length > 1 ? parts.slice(1).join(" ") : m.last_name || "",
        )
        if (first) infos.push({ id: key, first, last })
      }
    })
    // manual senders
    manualSenders.forEach((s) => {
      const key = `manual:${s.id}`
      if (selectedSenders[key]) {
        infos.push({
          id: key,
          first: norm(s.firstName),
          last: norm(s.lastName),
        })
      }
    })
    return infos
  }, [teamMembers, selectedSenders, manualSenders])

  const emailAccountsCount = useMemo(() => {
    const emails = new Set<string>()

    preview.forEach((row) => {
      if (row.status === "ready") {
        const local = (row as any).local || ""
        const domain = (row as any).domain || ""
        const email =
          `${local}`.trim().toLowerCase() +
          "@" +
          `${domain}`.trim().toLowerCase()
        if (local && domain) emails.add(email)
      }
    })

    manualAccounts.forEach((a) => {
      if (a.status === "ready") {
        const local = (a.local || "").trim().toLowerCase()
        const domain = (a.domain || "").trim().toLowerCase()
        if (local && domain) {
          const email = `${local}@${domain}`
          emails.add(email)
        }
      }
    })

    return emails.size
  }, [preview, manualAccounts])

  // Email accounts list to send to Checkout (merge preview + manual-ready, deduped)
  const checkoutEmailAccounts = useMemo(() => {
    const seen = new Set<string>()
    const result: Array<{ email: string; name: string; signature?: string }> =
      []

    // From preview (ready rows)
    preview.forEach((row) => {
      if (row.status === "ready" && (row as any).local && (row as any).domain) {
        const local = (row as any).local as string
        const domain = (row as any).domain as string
        const first = (row as any).first as string
        const last = (row as any).last as string
        const signatureValue =
          ((row as any).signature as string | undefined) || signature || ""
        const email = `${local.trim().toLowerCase()}@${domain.trim().toLowerCase()}`
        if (!seen.has(email)) {
          seen.add(email)
          result.push({
            email,
            name: `${first} ${last}`.trim(),
            signature: signatureValue,
          })
        }
      }
    })

    // From manual accounts (ready rows not already in preview)
    manualAccounts.forEach((a) => {
      if (a.status === "ready") {
        const local = (a.local || "").trim()
        const domain = (a.domain || "").trim()
        if (!local || !domain) return
        const email = `${local.toLowerCase()}@${domain.toLowerCase()}`
        if (!seen.has(email)) {
          seen.add(email)
          const name =
            `${(a.first || "").trim()} ${(a.last || "").trim()}`.trim()
          result.push({
            email,
            name,
            signature: a.signature || signature || "",
          })
        }
      }
    })

    return result
  }, [preview, manualAccounts, signature])

  // Atomic price validation: check and set error immediately if invalid
  const priceError = useMemo(() => {
    if (
      emailAccountsCount > 0 &&
      (googlePriceCents == null || googlePriceCents <= 0)
    ) {
      return window.i18n.t("js.email_accounts.provisioning.price_not_available")
    }
    return undefined
  }, [emailAccountsCount, googlePriceCents])

  // Set error atomically when price is invalid
  useEffect(() => {
    if (priceError) {
      setFlowError(priceError)
    } else {
      // Clear error if it was the price error
      setFlowError((prev) => {
        if (
          prev ===
          window.i18n.t("js.email_accounts.provisioning.price_not_available")
        ) {
          return undefined
        }
        return prev
      })
    }
  }, [priceError])

  // Total price for email accounts
  // Use backend-provided prices in user's currency when available
  const emailAccountsTotalPriceCents = useMemo(() => {
    if (emailAccountsCount <= 0) return 0

    // Atomic check: if price is invalid, return 0 (error is set above)
    if (googlePriceCents == null || googlePriceCents <= 0) {
      return 0
    }

    return emailAccountsCount * googlePriceCents
  }, [emailAccountsCount, googlePriceCents])

  const canPreview = useMemo(() => {
    if (!manualMode) {
      const validation = validateAutoMode({
        selectedSendersCount: selectedSenderInfos.length,
        domainsCount: selectedDomains.length,
        qty: emailAccountsPerSender,
        signature,
      })
      return validation.isValid
    }

    const allowedDomains = selectedDomains.map((d) => (d || "").toLowerCase())
    const hasValidAccount = manualAccounts.some((a) => {
      const hasData =
        (a.first || "").trim() ||
        (a.last || "").trim() ||
        (a.local || "").trim() ||
        (a.domain || "").trim() ||
        (a.signature || "").trim()

      if (!hasData) return false

      const validation = validateManualAccount(
        {
          id: a.id,
          first: a.first || "",
          last: a.last || "",
          local: a.local || "",
          domain: a.domain || "",
          signature: a.signature || "",
        },
        {
          allowedDomains,
          allowGlobalSignature: true,
          globalSignature: signature || "",
        },
      )

      return validation.isValid
    })

    const hasProvider = true // Manual mode doesn't require provider selection
    const hasQty = true // Manual mode doesn't require qty selection
    return hasProvider && hasValidAccount && hasQty
  }, [
    manualMode,
    manualAccounts,
    selectedSenderInfos,
    emailAccountsPerSender,
    selectedDomains,
    signature,
  ])

  const { buildPreview, duplicateEmailMap } = useAccountPreview({
    manualMode,
    manualAccounts,
    selectedSenderInfos,
    accountGenerationDomains,
    emailAccountsPerSender,
    signature,
    preview,
    setPreview,
    setShowPreview,
    existingEmails: existingMailboxes,
  })

  // Reset Accounts step when selected domains change (but not on initial mount when restoring from storage)
  const prevDomainsRef = useRef<string | null>(null)
  const isRestoringRef = useRef<boolean>(true)
  useEffect(() => {
    // Allow a brief delay for storage restoration to complete
    if (isRestoringRef.current) {
      setTimeout(() => {
        isRestoringRef.current = false
      }, 100)
      return
    }
    const current = selectedDomains.join("|")
    if (prevDomainsRef.current === null) {
      prevDomainsRef.current = current
      return
    }
    if (prevDomainsRef.current !== current) {
      prevDomainsRef.current = current
      // reset only the Accounts step state; keep domain selections from the previous step
      setSelectedSenders({})
      setManualSenders([])
      setShowManualSenderForm(false)
      setManualFirst("")
      setManualLast("")
      setEmailAccountsPerSender(1)
      setSignature("")
      setPreview([])
      setShowPreview(false)
      setAccountGenerationMap({})
      setManualAccounts([
        {
          id: `${Date.now()}`,
          first: "",
          last: "",
          local: "",
          domain: "",
          signature: "",
          open: true,
          status: "draft",
        },
      ])
      // If user is on a later tab, ensure they revisit Accounts with fresh state
      setTab((prev) =>
        prev === "accounts" || prev === "domain" ? prev : "accounts",
      )
    }
  }, [selectedDomains])

  const duplicateEmailSet = useMemo(() => {
    return new Set(
      Object.entries(duplicateEmailMap)
        .filter(([, count]) => count > 1)
        .map(([email]) => email),
    )
  }, [duplicateEmailMap])

  const accountsValidation = useMemo(() => {
    const allowedDomains = selectedDomains.map((d) => (d || "").toLowerCase())
    const isDuplicate = (local?: string, domain?: string) => {
      if (!local || !domain) return false
      return duplicateEmailSet.has(
        `${local.trim().toLowerCase()}@${domain.trim().toLowerCase()}`,
      )
    }

    const previewReadyCount = preview.filter((r) => r.status === "ready").length

    const previewInvalidCount = preview.filter((r) => {
      if (r.status !== "ready") return false
      const ready = r as any
      const hasData =
        (ready.first || "").trim() ||
        (ready.last || "").trim() ||
        (ready.local || "").trim() ||
        (ready.domain || "").trim() ||
        (ready.signature || "").trim()
      if (!hasData) return false
      if (isDuplicate(ready.local, ready.domain)) return false
      const validation = validateManualAccount(
        {
          id: ready.id,
          first: ready.first || "",
          last: ready.last || "",
          local: ready.local || "",
          domain: ready.domain || "",
          signature: ready.signature || "",
        },
        {
          allowedDomains,
          allowGlobalSignature: true,
          globalSignature: signature || "",
        },
      )
      return !validation.isValid
    }).length

    const manualReadyCount = manualAccounts.filter(
      (a) => a.status === "ready",
    ).length

    const manualInvalidCount = manualAccounts.filter((a) => {
      if (a.status !== "ready") return false
      const hasData =
        (a.first || "").trim() ||
        (a.last || "").trim() ||
        (a.local || "").trim() ||
        (a.domain || "").trim() ||
        (a.signature || "").trim()
      if (!hasData) return false
      if (isDuplicate(a.local, a.domain)) return false
      const validation = validateManualAccount(
        {
          id: a.id,
          first: a.first || "",
          last: a.last || "",
          local: a.local || "",
          domain: a.domain || "",
          signature: a.signature || "",
        },
        {
          allowedDomains,
          allowGlobalSignature: true,
          globalSignature: signature || "",
        },
      )
      return !validation.isValid
    }).length

    return {
      previewReadyCount,
      previewInvalidCount,
      manualReadyCount,
      manualInvalidCount,
    }
  }, [preview, manualAccounts, selectedDomains, signature, duplicateEmailSet])

  const isCheckoutDisabled = useMemo(() => {
    // Check if domains are selected
    if (selectedDomains.length === 0) return true

    // Skip accounts check for domains_only flow
    if (flowMode === "domains_only") return false

    // Check if accounts step is completed (has ready accounts in preview or manualAccounts)
    const totalReadyCount =
      accountsValidation.previewReadyCount + accountsValidation.manualReadyCount
    if (totalReadyCount === 0) return true

    // Check if all ready preview accounts are valid
    if (
      accountsValidation.previewInvalidCount > 0 ||
      accountsValidation.manualInvalidCount > 0
    )
      return true

    // Check if billing step is completed
    if (!domainOwner) return true
    if (!currentBillingInformation) return true
    if (!currentCreditCard) return true
    if (!domainOwnerConfirmed) return true

    return false
  }, [
    selectedDomains,
    preview,
    manualAccounts,
    domainOwner,
    currentBillingInformation,
    currentCreditCard,
    domainOwnerConfirmed,
    flowMode,
    accountsValidation.previewReadyCount,
    accountsValidation.manualReadyCount,
    accountsValidation.previewInvalidCount,
    accountsValidation.manualInvalidCount,
  ])

  const goToTab = (next: Tab) => {
    if (next === "accounts" && selectedDomains.length === 0) {
      setFlowError(
        window.i18n.t(
          "js.email_accounts.provisioning.select_at_least_one_domain",
        ),
      )
      window.setTimeout(() => setFlowError(undefined), 4000)
      return
    }

    if (next === "checkout") {
      if (isCheckoutDisabled) {
        if (selectedDomains.length === 0) {
          setFlowError(
            window.i18n.t(
              "js.email_accounts.provisioning.select_at_least_one_domain",
            ),
          )
        } else if (!domainOwner) {
          setFlowError(
            window.i18n.t(
              "js.email_accounts.provisioning.domain_owner_required",
            ),
          )
        } else if (!currentBillingInformation) {
          setFlowError(
            window.i18n.t(
              "js.email_accounts.provisioning.billing_information_required",
            ),
          )
        } else if (!currentCreditCard) {
          setFlowError(
            window.i18n.t(
              "js.email_accounts.provisioning.payment_method_required",
            ),
          )
        } else if (!domainOwnerConfirmed) {
          setFlowError(
            window.i18n.t(
              "js.email_accounts.provisioning.domain_owner_confirmation_required",
            ),
          )
        } else {
          const previewReadyCount = preview.filter(
            (r) => r.status === "ready",
          ).length
          const manualReadyCount = manualAccounts.filter(
            (a) => a.status === "ready",
          ).length
          const totalReadyCount = previewReadyCount + manualReadyCount
          if (totalReadyCount === 0) {
            setFlowError(
              window.i18n.t(
                "js.email_accounts.provisioning.preview_ready_required",
              ),
            )
          } else {
            setFlowError(
              window.i18n.t(
                "js.email_accounts.provisioning.complete_all_accounts_required",
              ),
            )
          }
        }
        window.setTimeout(() => setFlowError(undefined), 4000)
        return
      }
    }
    setFlowError(undefined)
    setTab(next)
  }

  const goToNextTab = () => {
    const next = getNextTab(tab)
    if (next !== tab) {
      goToTab(next)
    }
  }

  const removeSuggestion = (id: string) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== id))
    setSelectedMap((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const addManualDomain = async () => {
    if (!manualDomain.trim() || isCheckingManual) return
    setIsCheckingManual(true)
    setManualError(undefined)

    const result = await addManualDomainToSuggestions({
      manualDomain,
      suggestions,
      selectedMap,
      setSelectedMap,
    })

    if (!result.success) {
      setManualError(result.error)
      setIsCheckingManual(false)
      return
    }

    setManualDomain("")
    setShowManualForm(false)
    setIsCheckingManual(false)
  }

  const onGenerateSuggestions = async (opts: { replace?: boolean } = {}) => {
    await generateDomainSuggestions({
      baseDomain,
      domainsCount,
      replace: opts.replace,
    })
  }

  // Note: State persistence is handled by useProvisioningStorage hook above
  // It automatically saves suggestions (including newly generated/added ones) whenever they change
  return (
    <Tabs
      value={tab}
      onValueChange={(next) => goToTab(next as Tab)}
      className="h-tabs--lg"
    >
      <TabsList className="px-6 mb-10">
        <TabsListItem>
          <TabsTrigger
            value="domain"
            stepNumber={
              isStepCompleted(tab, "domain")
                ? undefined
                : getStepNumber("domain")
            }
            completed={isStepCompleted(tab, "domain")}
          >
            {window.i18n.t("js.email_accounts.provisioning.tabs.domain")}
          </TabsTrigger>
        </TabsListItem>
        {flowMode !== "domains_only" && (
          <TabsListItem>
            <TabsTrigger
              value="accounts"
              disabled={
                selectedDomains.length === 0 &&
                tab !== "accounts" &&
                preview.length === 0 &&
                !manualAccounts.some((a) => a.status === "ready")
              }
              stepNumber={
                isStepCompleted(tab, "accounts")
                  ? undefined
                  : getStepNumber("accounts")
              }
              completed={isStepCompleted(tab, "accounts")}
            >
              {window.i18n.t("js.email_accounts.provisioning.tabs.accounts")}
            </TabsTrigger>
          </TabsListItem>
        )}
        <TabsListItem>
          <TabsTrigger
            value="billing"
            stepNumber={
              isStepCompleted(tab, "billing")
                ? undefined
                : getStepNumber("billing")
            }
            completed={isStepCompleted(tab, "billing")}
          >
            {window.i18n.t("js.email_accounts.provisioning.tabs.domain_owner")}
          </TabsTrigger>
        </TabsListItem>
        <TabsListItem>
          <TabsTrigger
            value="checkout"
            disabled={isCheckoutDisabled && tab !== "checkout"}
            stepNumber={
              isStepCompleted(tab, "checkout")
                ? undefined
                : getStepNumber("checkout")
            }
            completed={isStepCompleted(tab, "checkout")}
          >
            {window.i18n.t("js.email_accounts.provisioning.tabs.checkout")}
          </TabsTrigger>
        </TabsListItem>
        {tab !== "checkout" && (
          <TabsListItem className="ml-auto d-flex align-items-center align-self-center">
            {(newDomainsForCheckout.length > 0 && totalPriceCents > 0) ||
            emailAccountsTotalPriceCents > 0 ? (
              <div className="mr-4">
                <OrderSummaryPopover
                  selectedDomainsCount={newDomainsForCheckout.length}
                  domainsTotalPriceCents={totalPriceCents}
                  emailAccountsCount={emailAccountsCount}
                  emailAccountsTotalPriceCents={emailAccountsTotalPriceCents}
                  currencyCode={currencyCode}
                />
              </div>
            ) : null}

            {(() => {
              const computedReason = computeNextDisabledReason({
                tab: tab as any,
                selectedDomainsCount: selectedDomains.length,
                manualMode,
                selectedSendersCount: selectedSenderInfos.length,
                accountGenerationDomainsCount: accountGenerationDomains.length,
                emailAccountsPerSender,
                signature,
                manualAccounts,
                previewReadyCount: preview.filter((r) => r.status === "ready")
                  .length,
                previewInvalidCount: preview.filter((r) => {
                  if (r.status !== "ready") return false
                  const ready = r as any
                  const hasData =
                    (ready.first || "").trim() ||
                    (ready.last || "").trim() ||
                    (ready.local || "").trim() ||
                    (ready.domain || "").trim() ||
                    (ready.signature || "").trim()
                  if (!hasData) return false
                  const validation = validateManualAccount(
                    {
                      id: ready.id,
                      first: ready.first || "",
                      last: ready.last || "",
                      local: ready.local || "",
                      domain: ready.domain || "",
                      signature: ready.signature || "",
                    },
                    {
                      allowedDomains: selectedDomains.map((d) =>
                        (d || "").toLowerCase(),
                      ),
                      allowGlobalSignature: true,
                      globalSignature: signature || "",
                    },
                  )
                  return !validation.isValid
                }).length,
                allowedDomains: selectedDomains,
                domainOwner,
                billingInformation: currentBillingInformation,
                creditCard: currentCreditCard,
                domainOwnerConfirmed,
                flowMode,
              })
              const isDisabled = !!computedReason

              return (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={goToNextTab}
                  disabled={isDisabled}
                >
                  {window.i18n.t("js.email_accounts.provisioning.next")}{" "}
                  <span
                    className="far fa-arrow-right"
                    aria-hidden="true"
                  ></span>
                </Button>
              )
            })()}
          </TabsListItem>
        )}
      </TabsList>
      <TabsContent value="domain">
        <DomainsTab
          baseDomain={baseDomain}
          domainsCount={domainsCount}
          suggestions={suggestions}
          selectedMap={selectedMap}
          showManualForm={showManualForm}
          manualDomain={manualDomain}
          isGenerating={isGenerating}
          isCheckingManual={isCheckingManual}
          manualError={manualError}
          flowError={flowError}
          redirectUrl={redirectUrl}
          isWebmail={isWebmail}
          onBaseDomainChange={(v) => setBaseDomain(v)}
          onDomainsCountChange={(n) => setDomainsCount(n)}
          onToggleManualForm={(show) => setShowManualForm(show)}
          onManualDomainChange={(v) => {
            setManualDomain(v)
            if (manualError) setManualError(undefined)
          }}
          onAddManualDomain={addManualDomain}
          onGenerateSuggestions={onGenerateSuggestions}
          onRemoveSuggestion={removeSuggestion}
          onToggleSuggestion={(id, checked) =>
            setSelectedMap((prev) => ({ ...prev, [id]: checked }))
          }
          onRedirectUrlChange={(v) => setRedirectUrl(v)}
          currencyCode={currencyCode}
          existingDomains={existingDomains}
          flowMode={flowMode}
        />
      </TabsContent>
      {flowMode !== "domains_only" && (
        <TabsContent value="accounts">
          <AccountsTab
            teamMembers={teamMembers}
            manualMode={manualMode}
            setManualMode={setManualMode}
            selectedSenders={selectedSenders}
            setSelectedSenders={setSelectedSenders}
            manualSenders={manualSenders}
            setManualSenders={setManualSenders}
            manualFirst={manualFirst}
            setManualFirst={setManualFirst}
            manualLast={manualLast}
            setManualLast={setManualLast}
            manualSenderError={manualSenderError}
            setManualSenderError={setManualSenderError}
            showManualSenderForm={showManualSenderForm}
            setShowManualSenderForm={setShowManualSenderForm}
            selectedDomains={selectedDomains}
            suggestions={suggestions}
            accountGenerationMap={accountGenerationMap}
            setAccountGenerationMap={setAccountGenerationMap}
            emailAccountsPerSender={emailAccountsPerSender}
            setEmailAccountsPerSender={setEmailAccountsPerSender}
            signature={signature}
            setSignature={setSignature}
            canPreview={canPreview}
            buildPreview={buildPreview}
            manualAccounts={manualAccounts}
            setManualAccounts={(next) => setManualAccounts(next)}
            duplicateEmailMap={duplicateEmailMap}
            preview={preview}
            setPreview={(next) => setPreview(next)}
            showPreview={showPreview}
            setShowPreview={setShowPreview}
            currencyCode={currencyCode}
            googlePriceCents={googlePriceCents}
          />
        </TabsContent>
      )}
      <TabsContent value="billing">
        <BillingTab
          user={user}
          domainOwner={domainOwner}
          setDomainOwner={setDomainOwner}
          domainOwnerConfirmed={domainOwnerConfirmed}
          setDomainOwnerConfirmed={setDomainOwnerConfirmed}
          editingDomainOwner={editingDomainOwner}
          setEditingDomainOwner={setEditingDomainOwner}
          domainOwnerForm={domainOwnerForm}
          setDomainOwnerForm={setDomainOwnerForm}
          savingDomainOwner={savingDomainOwner}
          setSavingDomainOwner={setSavingDomainOwner}
          countryOptions={countryOptions}
          billingInformation={currentBillingInformation}
          creditCard={currentCreditCard}
          setBillingInformation={setCurrentBillingInformation}
          setCreditCard={setCurrentCreditCard}
          billingForm={billingForm}
          setBillingForm={setBillingForm}
          paymentForm={paymentForm}
          setPaymentForm={setPaymentForm}
          editingBilling={editingBilling}
          setEditingBilling={setEditingBilling}
          editingPayment={editingPayment}
          setEditingPayment={setEditingPayment}
          stripePublicKey={stripePublicKey}
        />
      </TabsContent>
      <TabsContent value="checkout">
        <CheckoutTab
          domainCount={newDomainsForCheckout.length}
          emailCount={emailAccountsCount}
          domains={newDomainsForCheckout}
          emailAccounts={checkoutEmailAccounts}
          paymentMethodId={currentCreditCard?.payment_method_id}
          billingInformation={currentBillingInformation}
          creditCard={currentCreditCard}
          currencyCode={currencyCode}
          provider="google"
          billingCycle="monthly"
          emailPriceCents={googlePriceCents}
          redirectUrl={redirectUrl}
          onGoToDomainSelection={() => setTab("domain")}
          domainOwnerForm={domainOwnerForm}
          onOrderComplete={(orderId) => {
            // Reset the wizard for a new order
            resetAll()
            // Redirect to order completed page
            window.location.href = `/email_accounts/orders/${orderId}`
          }}
        />
      </TabsContent>
    </Tabs>
  )
}

export default EmailAccountsProvisioning
